import { useEffect, useRef, useState } from 'react';
import type { AppState } from '../types';
import { emptyState, seedState, serializeState } from '../store';
import { createSnapshot, fetchState, putState } from './api';

export type SyncStatus =
  | 'boot' // читаем состояние с сервера
  | 'local' // сервер недоступен или пользователь не вошёл — работаем локально
  | 'saving'
  | 'synced'
  | 'conflict'
  | 'rejected';

export interface SyncState {
  status: SyncStatus;
  rev: number;
  updatedAt: number | null;
  errors: string[];
}

export interface ServerSyncOptions {
  /** Синхронизация включается только после входа: у гостя нет своего хранилища. */
  enabled: boolean;
  /** id пользователя: смена значения перечитывает состояние с сервера. */
  sessionKey: string | null;
  /**
   * Открытая поездка. null — поездок ещё нет: сервер сам решит, куда писать,
   * и заведёт первую. Смена значения перечитывает состояние так же, как смена
   * пользователя: это другой документ, а не другая ревизия того же.
   */
  tripId?: string | null;
  /**
   * Поездку правит не только эта вкладка (есть соавторы либо она чужая) —
   * тогда состояние ещё и периодически перечитывается: иначе чужие правки
   * были бы видны только после перезагрузки страницы.
   */
  poll?: boolean;
  /** Подпись снимка, в который уезжают гостевые данные при входе. */
  guestBackupLabel: string;
}

const DEBOUNCE_MS = 700;

/**
 * Как часто перечитывается общая поездка. Реального времени здесь не нужно —
 * люди правят маршрут, а не печатают в один документ; чаще опрашивать сервер
 * значит греть его ради секунд.
 */
const POLL_MS = 15_000;

/**
 * Двусторонняя синхронизация состояния с сервером (SQLite).
 *
 * После входа: если на сервере есть состояние — оно авторитетно и подставляется
 * в стор. Дальше каждое изменение уезжает PUT-ом с ревизией (оптимистичная
 * блокировка). Конфликт 409 разрешается в пользу сервера — состояние из ответа
 * применяется, статус переходит в 'conflict', чтобы UI мог сказать об этом.
 *
 * Без входа (или при недоступном сервере) статус 'local': приложение работает
 * на StorageAdapter (localStorage / память), ничего не отправляя.
 */
export function useServerSync(
  state: AppState,
  adopt: (s: AppState) => void,
  options: ServerSyncOptions,
): SyncState {
  const { enabled, sessionKey, tripId = null, poll = false, guestBackupLabel } = options;

  const [sync, setSync] = useState<SyncState>({
    status: 'boot',
    rev: 0,
    updatedAt: null,
    errors: [],
  });

  const revRef = useRef(0);
  const readyRef = useRef(false);
  const availableRef = useRef(false);
  /**
   * Счётчик завершённых загрузок. Готовность живёт в ref-ах, а их изменение
   * рендер не вызывает: без этого счётчика эффект отправки не перезапускался
   * после загрузки, и состояние уезжало на сервер только со следующей правкой.
   * На пустом сервере это значило, что до первого касания там не появлялось
   * ничего — включая саму поездку в списке.
   */
  const [loadedAt, setLoadedAt] = useState(0);
  /** состояние, которое сейчас летит на сервер — чтобы не гонять его повторно */
  const inFlightRef = useRef<string | null>(null);
  /** свежее состояние без перезапуска эффекта загрузки */
  const stateRef = useRef(state);
  stateRef.current = state;
  const backupLabelRef = useRef(guestBackupLabel);
  backupLabelRef.current = guestBackupLabel;
  /** Чей сеанс уже загружали: отличает вход в аккаунт от переключения поездки. */
  const loadedSessionRef = useRef<string | null>(null);

  // Первичная загрузка — заново на каждый вход и выход
  useEffect(() => {
    let cancelled = false;

    readyRef.current = false;
    availableRef.current = false;
    inFlightRef.current = null;
    revRef.current = 0;

    if (!enabled) {
      loadedSessionRef.current = null;
      setSync({ status: 'local', rev: 0, updatedAt: null, errors: [] });
      return undefined;
    }

    // Гостевые данные спасаются один раз — при входе. Переключение поездки
    // тоже проходит здесь, но локальное состояние там принадлежит предыдущей
    // поездке, и снимать с него «резервную копию гостя» было бы ложью.
    const firstLoadOfSession = loadedSessionRef.current !== sessionKey;
    loadedSessionRef.current = sessionKey;

    setSync({ status: 'boot', rev: 0, updatedAt: null, errors: [] });

    void (async () => {
      const local = stateRef.current;
      const remote = await fetchState(tripId);
      if (cancelled) return;

      if (!remote) {
        readyRef.current = true;
        setSync({ status: 'local', rev: 0, updatedAt: null, errors: [] });
        return;
      }

      availableRef.current = true;

      if (remote.exists && remote.state) {
        // Серверная версия побеждает, но работа, сделанная до входа, не должна
        // испариться: кладём её снимком, откуда её видно в истории.
        const localText = serializeState(local);
        const untouched = localText === serializeState(seedState());
        if (firstLoadOfSession && !untouched && localText !== serializeState(remote.state)) {
          void createSnapshot(backupLabelRef.current, local, tripId);
        }

        revRef.current = remote.rev;
        adopt(remote.state);
        inFlightRef.current = JSON.stringify(remote.state);
        readyRef.current = true;
        setLoadedAt((n) => n + 1);
        setSync({
          status: 'synced',
          rev: remote.rev,
          updatedAt: remote.updatedAt ?? null,
          errors: [],
        });
      } else if (tripId) {
        // Названной поездки на сервере нет — значит, её только что удалили в
        // другой вкладке. Заливать в неё содержимое предыдущей нельзя: это
        // чужой документ, а не пустая ревизия этого.
        const blank = emptyState();
        revRef.current = 0;
        adopt(blank);
        inFlightRef.current = JSON.stringify(blank);
        readyRef.current = true;
        setLoadedAt((n) => n + 1);
        setSync({ status: 'saving', rev: 0, updatedAt: null, errors: [] });
      } else {
        // Поездок ещё нет вовсе — текущее локальное состояние станет первой.
        revRef.current = 0;
        readyRef.current = true;
        setLoadedAt((n) => n + 1);
        setSync({ status: 'saving', rev: 0, updatedAt: null, errors: [] });
      }
    })();

    return () => {
      cancelled = true;
    };
    // adopt стабилен (useCallback в App); перечитываем на смену пользователя
    // и на смену открытой поездки — это другой документ, а не другая ревизия.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sessionKey, tripId]);

  // Отправка изменений
  useEffect(() => {
    if (!enabled || !readyRef.current || !availableRef.current) return;

    const serialized = JSON.stringify(state);
    if (serialized === inFlightRef.current) return;

    const timer = setTimeout(() => {
      inFlightRef.current = serialized;
      setSync((prev) => ({ ...prev, status: 'saving', errors: [] }));

      void putState(state, revRef.current, tripId).then((res) => {
        if (res.ok) {
          revRef.current = res.rev;
          setSync({ status: 'synced', rev: res.rev, updatedAt: res.updatedAt, errors: [] });
          return;
        }

        if (res.kind === 'conflict') {
          revRef.current = res.rev;
          inFlightRef.current = JSON.stringify(res.state);
          adopt(res.state);
          // Своего текста здесь нет намеренно: про конфликт всё уже сказано
          // подписью самого статуса (sync.conflict.tip) — на пяти языках, а не
          // на одном. errors несёт только то, что пришло от сервера.
          setSync({ status: 'conflict', rev: res.rev, updatedAt: null, errors: [] });
          return;
        }

        inFlightRef.current = null;
        if (res.kind === 'network' || res.kind === 'unauthorized') {
          // Сессия кончилась или сети нет — дальше работаем локально, не теряя
          // изменений: они останутся в localStorage.
          availableRef.current = false;
          setSync((prev) => ({ ...prev, status: 'local', errors: res.errors }));
        } else {
          setSync((prev) => ({ ...prev, status: 'rejected', errors: res.errors }));
        }
      });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [state, adopt, enabled, tripId, loadedAt]);

  // Чужие правки в общей поездке
  useEffect(() => {
    if (!enabled || !poll) return undefined;

    /** Своя несохранённая правка есть — чужая версия сейчас затёрла бы её. */
    const dirty = () => JSON.stringify(stateRef.current) !== inFlightRef.current;

    const timer = setInterval(() => {
      if (!readyRef.current || !availableRef.current || dirty()) return;
      void fetchState(tripId).then((remote) => {
        if (!remote?.exists || !remote.state) return;
        // Ревизия своя же или старее — перерисовывать нечего. Успели тронуть
        // состояние, пока летел ответ, — правка человека важнее.
        if (remote.rev <= revRef.current || dirty()) return;
        revRef.current = remote.rev;
        inFlightRef.current = JSON.stringify(remote.state);
        adopt(remote.state);
        setSync({
          status: 'synced',
          rev: remote.rev,
          updatedAt: remote.updatedAt ?? null,
          errors: [],
        });
      });
    }, POLL_MS);

    return () => clearInterval(timer);
  }, [enabled, poll, tripId, adopt, loadedAt]);

  return sync;
}
